import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { AnimatePresence, motion } from 'framer-motion';
import { Truck, Loader2, ArrowRight, Link2, Check, X } from 'lucide-react';
import { Reveal, EASE } from './reveal';
import { track } from '../lib/track';

const API = process.env.REACT_APP_BACKEND_URL;
const VALID_MODELS = new Set(['8x24', '10x30']);

function fmt(n) {
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}

// Human-friendly hull label used in the quote prefill message.
const MODEL_LABEL = {
  '8x24': "8' \u00d7 24'",
  '10x30': "10' \u00d7 30' (wide load)",
};

function buildQuoteMessage({ zip, model, result }) {
  const hull = MODEL_LABEL[model] || model;
  const dest =
    result.dest_city && result.dest_state
      ? `${result.dest_city}, ${result.dest_state} (ZIP ${zip})`
      : `ZIP ${zip}`;
  return (
    `Hi, I'd like a quote on a ${hull} HDPE barge delivered to ${dest}. ` +
    `Your online estimator showed a shipping range of ${fmt(result.low)}\u2013${fmt(result.high)} ` +
    `(${result.distance_miles} mi from Fort Myers). Please confirm final delivery pricing and next steps.`
  );
}

// Pull ?zip=&hull= off the current URL so we can rehydrate a shared estimate.
function readParamsFromUrl() {
  if (typeof window === 'undefined') return { zip: '', hull: '' };
  const p = new URLSearchParams(window.location.search);
  const zip = (p.get('zip') || '').replace(/\D/g, '').slice(0, 5);
  const hull = p.get('hull') || '';
  return {
    zip: /^\d{5}$/.test(zip) ? zip : '',
    hull: VALID_MODELS.has(hull) ? hull : '',
  };
}

export default function ShippingEstimator() {
  const initial = readParamsFromUrl();
  const [zip, setZip] = useState(initial.zip);
  const [model, setModel] = useState(initial.hull || '10x30');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [stickyVisible, setStickyVisible] = useState(false);
  const [stickyDismissed, setStickyDismissed] = useState(false);
  // Track the two viewport signals separately so the bar shows only when
  // the panel is above the fold AND the contact section is still below.
  const panelPastRef = useRef(false);
  const contactReachedRef = useRef(false);
  const copyTimer = useRef(null);
  const rootRef = useRef(null);

  const runEstimate = useCallback(async (zipVal, modelVal) => {
    setError('');
    setResult(null);
    if (!/^\d{5}$/.test(zipVal)) {
      setError('Please enter a valid 5-digit US ZIP code.');
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/api/shipping/estimate`, { zip: zipVal, model: modelVal });
      setResult(data);
      // Cache the freshest estimate so it can be attached to any lead
      // submitted from the quote form. Kept lightweight (~200 B) and
      // scoped to this browser.
      try {
        window.localStorage.setItem(
          'last_estimate',
          JSON.stringify({
            zip: zipVal,
            model: modelVal,
            distance_miles: data.distance_miles,
            dest_city: data.dest_city,
            dest_state: data.dest_state,
            low: data.low,
            high: data.high,
            at: new Date().toISOString(),
          })
        );
      } catch (_) {}
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not estimate shipping. Try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  // If the page loads with a valid ?zip=&hull= pair, auto-run the estimate
  // and scroll the panel into view so a shared link lands the recipient
  // directly on the answer.
  useEffect(() => {
    if (initial.zip && initial.hull) {
      runEstimate(initial.zip, initial.hull);
      if (rootRef.current) {
        rootRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => () => {
    if (copyTimer.current) clearTimeout(copyTimer.current);
  }, []);

  // Show the sticky bar once the estimator panel has scrolled off the top
  // of the viewport AND the buyer hasn't reached the contact section yet.
  // Reset when a new estimate is run so dismissals don't carry over.
  useEffect(() => {
    setStickyDismissed(false);
  }, [result]);

  useEffect(() => {
    if (!result) {
      setStickyVisible(false);
      return undefined;
    }
    const panelEl = rootRef.current;
    const contactEl = document.getElementById('contact');
    if (!panelEl) return undefined;

    const recompute = () => {
      setStickyVisible(panelPastRef.current && !contactReachedRef.current);
    };

    // Panel observer: fires when the estimator has scrolled above the top.
    const panelObs = new IntersectionObserver(
      ([entry]) => {
        panelPastRef.current = entry.boundingClientRect.top < 0 && !entry.isIntersecting;
        recompute();
      },
      { threshold: 0, rootMargin: '0px' }
    );
    panelObs.observe(panelEl);

    // Contact observer: hides the bar once the buyer sees the quote form.
    let contactObs;
    if (contactEl) {
      contactObs = new IntersectionObserver(
        ([entry]) => {
          contactReachedRef.current = entry.isIntersecting;
          recompute();
        },
        { threshold: 0.05 }
      );
      contactObs.observe(contactEl);
    }

    return () => {
      panelObs.disconnect();
      if (contactObs) contactObs.disconnect();
    };
  }, [result]);

  const submit = (e) => {
    e.preventDefault();
    runEstimate(zip, model);
  };

  const copyShareLink = async () => {
    const url = new URL(window.location.href);
    url.search = '';
    url.searchParams.set('zip', zip);
    url.searchParams.set('hull', model);
    url.hash = 'coverage';
    const link = url.toString();
    try {
      await navigator.clipboard.writeText(link);
    } catch (_) {
      // Fallback for older browsers / insecure contexts.
      const ta = document.createElement('textarea');
      ta.value = link;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); } catch (_) {}
      document.body.removeChild(ta);
    }
    track('share_estimate', { location: 'shipping-estimator', model, zip });
    setCopied(true);
    if (copyTimer.current) clearTimeout(copyTimer.current);
    copyTimer.current = setTimeout(() => setCopied(false), 2200);
  };

  return (
    <Reveal delay={0.2}>
      <div ref={rootRef} className="mt-14 sm:mt-16 panel rounded-xl p-6 sm:p-7 md:p-8 scroll-mt-24">
        <div className="flex items-center gap-3 mb-4">
          <span className="h-px w-8 sm:w-10 bg-cyan-400" />
          <span className="font-tech text-cyan-300 text-[11px] uppercase tracking-[0.32em]">
            Delivery Estimate
          </span>
        </div>
        <h3 className="font-display text-white text-lg sm:text-xl font-semibold tracking-wide mb-4">
          Get a shipping estimate to your ZIP
        </h3>
        <p className="text-zinc-400 text-sm mb-5 max-w-2xl">
          Enter your ZIP and pick a barge size. We&apos;ll estimate the delivery cost from our Fort Myers, FL yard.
        </p>

        <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3">
          <input
            type="text"
            inputMode="numeric"
            maxLength={5}
            value={zip}
            onChange={(e) => setZip(e.target.value.replace(/\D/g, ''))}
            placeholder="ZIP code"
            className="w-full sm:w-40 bg-black/60 border border-white/10 focus:border-cyan-400/60 outline-none rounded-md px-4 py-3 text-white"
            data-testid="shipping-zip-input"
          />
          <select
            value={model}
            onChange={(e) => setModel(e.target.value)}
            className="w-full sm:w-64 bg-black/60 border border-white/10 focus:border-cyan-400/60 outline-none rounded-md px-4 py-3 text-white"
            data-testid="shipping-model-select"
          >
            <option value="8x24">8&prime; × 24&prime; (standard load)</option>
            <option value="10x30">10&prime; × 30&prime; (wide load)</option>
          </select>
          <button
            type="submit"
            disabled={loading}
            className="btn-teal px-5 py-3 rounded-md text-xs sm:text-sm uppercase tracking-[0.16em] whitespace-nowrap inline-flex items-center gap-2 justify-center disabled:opacity-60"
            data-testid="shipping-estimate-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Truck className="w-4 h-4" />}
            {loading ? 'Estimating' : 'Estimate'}
          </button>
        </form>

        {error && <p className="mt-4 text-red-400 text-sm" data-testid="shipping-error">{error}</p>}

        {result && (
          <div className="mt-5 pt-5 border-t border-white/5" data-testid="shipping-result">
            <div className="flex flex-wrap items-baseline gap-3">
              <span className="font-display text-white text-2xl sm:text-3xl font-bold">
                {fmt(result.low)} <span className="text-zinc-500 font-normal">–</span> {fmt(result.high)}
              </span>
              <span className="text-xs text-zinc-500">
                to {result.dest_city ? `${result.dest_city}, ${result.dest_state}` : `ZIP ${zip}`} · {result.distance_miles} mi
              </span>
            </div>
            <p className="mt-2 text-[11px] text-zinc-500 max-w-2xl">
              Estimate only. Final delivery cost is confirmed in writing based on exact route, permits, escort requirements, and current fuel. Wide-load surcharge already included for 10&prime; × 30&prime; hulls.
            </p>

            <div className="mt-5 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => {
                  const message = buildQuoteMessage({ zip, model, result });
                  track('buy_click', { location: 'shipping-estimator', model, zip });
                  window.dispatchEvent(
                    new CustomEvent('prefill-quote-message', {
                      detail: { message, source: 'shipping-estimator' },
                    })
                  );
                }}
                className="btn-teal px-5 py-2.5 rounded-md text-xs sm:text-sm uppercase tracking-[0.16em] inline-flex items-center gap-2"
                data-testid="shipping-add-to-quote-btn"
              >
                Add to my quote <ArrowRight className="w-4 h-4" />
              </button>

              <button
                type="button"
                onClick={copyShareLink}
                className="btn-ghost px-5 py-2.5 rounded-md text-xs sm:text-sm uppercase tracking-[0.16em] inline-flex items-center gap-2"
                data-testid="shipping-share-btn"
              >
                {copied ? (
                  <>
                    <Check className="w-4 h-4 text-cyan-300" />
                    <span className="text-cyan-300">Link copied</span>
                  </>
                ) : (
                  <>
                    <Link2 className="w-4 h-4" /> Copy share link
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>

      <AnimatePresence>
        {result && stickyVisible && !stickyDismissed && (
          <motion.div
            key="sticky-estimate"
            initial={{ y: 80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 80, opacity: 0 }}
            transition={{ duration: 0.35, ease: EASE }}
            className="fixed inset-x-0 bottom-3 sm:bottom-4 z-40 px-3 sm:px-5 pointer-events-none"
            data-testid="shipping-sticky-bar"
          >
            <div className="max-w-3xl mx-auto pointer-events-auto rounded-xl border border-cyan-400/30 bg-black/85 backdrop-blur-md shadow-[0_10px_40px_-10px_rgba(34,211,238,0.35)]">
              <div className="flex items-center gap-3 sm:gap-4 px-3 sm:px-4 py-2.5 sm:py-3">
                <div className="hidden sm:flex w-9 h-9 shrink-0 rounded-md bg-cyan-500/10 border border-cyan-400/30 items-center justify-center text-cyan-300">
                  <Truck className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="font-display text-white text-sm sm:text-base font-bold whitespace-nowrap">
                      {fmt(result.low)} <span className="text-zinc-500 font-normal">–</span> {fmt(result.high)}
                    </span>
                    <span className="text-[10px] sm:text-[11px] uppercase tracking-[0.16em] text-cyan-300/90 whitespace-nowrap">
                      {MODEL_LABEL[model] || model}
                    </span>
                  </div>
                  <div className="text-[10px] sm:text-[11px] text-zinc-500 truncate">
                    to {result.dest_city ? `${result.dest_city}, ${result.dest_state}` : `ZIP ${zip}`} · {result.distance_miles} mi from Fort Myers
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    const message = buildQuoteMessage({ zip, model, result });
                    track('buy_click', { location: 'shipping-estimator-sticky', model, zip });
                    window.dispatchEvent(
                      new CustomEvent('prefill-quote-message', {
                        detail: { message, source: 'shipping-estimator-sticky' },
                      })
                    );
                  }}
                  className="btn-teal shrink-0 px-3 sm:px-4 py-2 rounded-md text-[10px] sm:text-xs uppercase tracking-[0.16em] inline-flex items-center gap-1.5"
                  data-testid="shipping-sticky-cta"
                >
                  <span className="hidden sm:inline">Add to quote</span>
                  <span className="sm:hidden">Quote</span>
                  <ArrowRight className="w-3.5 h-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => setStickyDismissed(true)}
                  aria-label="Dismiss estimate"
                  className="shrink-0 text-zinc-500 hover:text-white transition-colors p-1"
                  data-testid="shipping-sticky-close"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </Reveal>
  );
}
